import { useCallback } from 'react';
import { useAuth } from '../context/BaseAuthProvider';
import { httpClient } from '../lib/requestFunctions';

export type GuidanceStep =
  | 'CLASS'
  | 'SUBJECT'
  | 'STRAND'
  | 'STRAND_UNIT'
  | 'INSERT'
  | null;

interface UseOnboardingGuidanceParams {
  selectedClass: string | null;
  selectedSubject: string | null;
  selectedStrand: string | null;
  selectedStrandUnit: string | null;
}

interface UseOnboardingGuidanceResult {
  isActive: boolean;
  currentStep: GuidanceStep;
  disable: () => Promise<void>;
  enable: () => Promise<void>;
}

export function useOnboardingGuidance({
  selectedClass,
  selectedSubject,
  selectedStrand,
  selectedStrandUnit,
}: UseOnboardingGuidanceParams): UseOnboardingGuidanceResult {
  const { user, refreshUser } = useAuth();

  const isActive = user?.guidanceEnabled ?? false;

  const currentStep: GuidanceStep = isActive
    ? !selectedClass
      ? 'CLASS'
      : !selectedSubject
        ? 'SUBJECT'
        : !selectedStrand
          ? 'STRAND'
          : !selectedStrandUnit
            ? 'STRAND_UNIT'
            : 'INSERT'
    : null;

  const setGuidanceEnabled = useCallback(
    async (enabled: boolean) => {
      await httpClient.patch('/user/preferences', { guidanceEnabled: enabled });
      await refreshUser();
    },
    [refreshUser],
  );

  const disable = useCallback(() => setGuidanceEnabled(false), [setGuidanceEnabled]);
  const enable = useCallback(() => setGuidanceEnabled(true), [setGuidanceEnabled]);

  return { isActive, currentStep, disable, enable };
}
